import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ensureUrl } from "@/lib/url";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Store, Plus, Trash2, Phone, Globe, ShoppingCart, MapPin, Clock, Search, Star, Loader2, Compass } from "lucide-react";
import ScanStrip from "@/components/ScanStrip";
import { toast } from "sonner";
import useVendorsData, { BLANK_VENDOR } from "@/hooks/useVendorsData";
import useDiscoverNearby from "@/hooks/useDiscoverNearby";

const TYPES = ["Grocery", "Supermarket", "Farmers Market", "Butcher", "Bakery", "Pharmacy", "Hardware", "Online store", "Gas supplier", "Other"];
const CATS = ["Produce", "Dairy", "Meat", "Bakery", "Pantry", "Frozen", "Beverages", "Snacks", "Household", "Personal Care"];
const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];

// Preset search URL templates for popular online stores. When the user picks
// one of these types, the search_url_template auto-fills. They can still edit
// it or add their own.
const ONLINE_PRESETS = [
  { label: "Amazon AU",       url: "https://www.amazon.com.au/s?k={q}" },
  { label: "Woolworths",      url: "https://www.woolworths.com.au/shop/search/products?searchTerm={q}" },
  { label: "Coles",           url: "https://www.coles.com.au/search?q={q}" },
  { label: "IGA",             url: "https://www.igashop.com.au/search?query={q}" },
  { label: "Chemist Warehouse", url: "https://www.chemistwarehouse.com.au/search?searchtext={q}" },
  { label: "Bunnings",        url: "https://www.bunnings.com.au/search/products?q={q}" },
  { label: "Costco AU",       url: "https://www.costco.com.au/search?text={q}" },
  { label: "eBay AU",         url: "https://www.ebay.com.au/sch/i.html?_nkw={q}" },
];

const TYPE_DOT = {
  "Grocery": "bg-hearth-clay",
  "Supermarket": "bg-hearth-slate",
  "Farmers Market": "bg-hearth-sage",
  "Butcher": "bg-hearth-coral",
  "Bakery": "bg-hearth-amber",
  "Pharmacy": "bg-hearth-sage",
  "Hardware": "bg-hearth-slate",
  "Online store": "bg-hearth-ink",
  "Other": "bg-hearth-mute",
};

export default function Vendors() {
  const {
    visibleItems,
    open, setOpen, editId,
    form, setForm,
    toggleCat, save, startEdit, startCreate, remove,
    load,
  } = useVendorsData();
  const blank = BLANK_VENDOR;
  const [scanConfidence, setScanConfidence] = useState(null);

  const applyScannedCard = (data) => {
    // Preserve existing hours/categories the user may have already ticked;
    // overwrite only the fields the AI extracted.
    setForm((prev) => ({
      ...prev,
      name: data.name || prev.name,
      type: data.type || prev.type,
      phone: data.phone || prev.phone,
      address: data.address || prev.address,
      website: data.website || prev.website,
      notes: data.notes ? (prev.notes ? `${prev.notes}\n${data.notes}` : data.notes) : prev.notes,
    }));
  };

  // -- Discover nearby (Google Places) --
  const discover = useDiscoverNearby({ onImported: load });
  const {
    open: discoverOpen, setOpen: setDiscoverOpen,
    query: discoverQuery, setQuery: setDiscoverQuery,
    locText: discoverLocText, setLocText: setDiscoverLocText,
    coords: discoverCoords, setCoords: setDiscoverCoords,
    loading: discoverLoading,
    results: discoverResults,
    importing,
    requestGeo,
    run: runDiscover,
    importOne,
    reset: resetDiscover,
  } = discover;

  const todayKey = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][new Date().getDay()];

  return (
    <div>
      <PageHeader
        eyebrow="Where to buy"
        title="Local vendors"
        subtitle="Shops you trust — opening hours, contact, and online ordering links."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="vendor-discover-button"
              onClick={() => { setDiscoverOpen(true); resetDiscover(); }}
              variant="ghost"
              className="rounded-full border border-hearth-line bg-hearth-paper text-hearth-ink hover:bg-hearth-stone px-4 h-10">
              <Compass size={15} className="mr-1.5" /> Discover nearby
            </Button>
            <Button data-testid="vendor-add-button" onClick={() => { setScanConfidence(null); startCreate(); }}
              className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white px-5 h-10">
              <Plus size={15} className="mr-1.5" /> Add vendor
            </Button>
          </div>
        }
      />

      {visibleItems.length === 0 ? (
        <EmptyState icon={Store} title="No vendors yet." message="Add the shops you actually use — supermarket, baker, butcher, farmers market — so grocery items can be tagged with where to buy them." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {visibleItems.map((v) => {
            const todayHours = v.hours?.[todayKey];
            return (
              <div key={v.id} data-testid={`vendor-card-${v.id}`} className="warm-card p-6 fade-up">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${TYPE_DOT[v.type] || "bg-hearth-mute"}`} />
                      <p className="caption">{v.type}{v.distance_km ? ` · ${v.distance_km} km` : ""}</p>
                    </div>
                    <h3 className="font-display text-3xl text-hearth-ink mt-1 leading-tight">{v.name}</h3>
                  </div>
                  <button data-testid={`vendor-delete-${v.id}`} onClick={() => remove(v.id)} className="p-2 rounded-md text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10">
                    <Trash2 size={15} />
                  </button>
                </div>

                {v.address && (
                  <p className="mt-3 text-sm text-hearth-mute flex items-start gap-1.5">
                    <MapPin size={13} className="mt-0.5 shrink-0" /> {v.address}
                  </p>
                )}

                <div className="mt-4 grid grid-cols-7 gap-1 text-[10px]">
                  {DAYS.map(([k, l]) => {
                    const open = !!v.hours?.[k];
                    const isToday = k === todayKey;
                    return (
                      <div
                        key={k}
                        className={`text-center rounded-md py-2 ${
                          isToday
                            ? "bg-hearth-clay/15 text-hearth-clayDark"
                            : "bg-hearth-stone/60 text-hearth-mute"
                        }`}
                        title={v.hours?.[k] ? `${l}: ${v.hours[k]}` : `${l}: closed`}
                      >
                        <div className={`uppercase tracking-wider ${isToday ? "font-semibold" : ""}`}>{l}</div>
                        <div
                          className={`mx-auto mt-1 w-1.5 h-1.5 rounded-full ${
                            open
                              ? isToday
                                ? "bg-hearth-clayDark"
                                : "bg-hearth-sageDark"
                              : "bg-hearth-mute/40"
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
                {todayHours && (
                  <div className="mt-2 text-xs text-hearth-mute inline-flex items-center gap-1.5">
                    <Clock size={12} /> <span>Open today: <span className="text-hearth-ink font-medium">{todayHours}</span></span>
                  </div>
                )}

                {v.categories?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {v.categories.map((c) => (
                      <span key={c} className="chip bg-hearth-sage/12 text-hearth-sageDark">{c}</span>
                    ))}
                  </div>
                )}

                <div className="mt-5 pt-4 border-t border-hearth-line/70 flex flex-wrap gap-2">
                  {v.phone && <a href={`tel:${v.phone}`} className="pill-btn bg-hearth-clay/10 text-hearth-clayDark hover:bg-hearth-clay/20 inline-flex items-center gap-1"><Phone size={13} /> Call</a>}
                  {v.online_order_url && <a href={ensureUrl(v.online_order_url)} target="_blank" rel="noreferrer" data-testid={`vendor-order-${v.id}`} className="pill-btn bg-hearth-ink text-white hover:bg-hearth-slateDark inline-flex items-center gap-1"><ShoppingCart size={13} /> Order online</a>}
                  {v.website && <a href={ensureUrl(v.website)} target="_blank" rel="noreferrer" data-testid={`vendor-website-${v.id}`} className="pill-btn border border-hearth-line bg-hearth-paper text-hearth-ink hover:bg-hearth-stone inline-flex items-center gap-1"><Globe size={13} /> Website</a>}
                  <Button variant="ghost" data-testid={`vendor-edit-${v.id}`} onClick={() => startEdit(v)} className="rounded-full text-sm ml-auto">Edit</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={discoverOpen} onOpenChange={setDiscoverOpen}>
        <DialogContent data-testid="discover-dialog" className="rounded-2xl border-hearth-line bg-hearth-paper max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Discover nearby vendors</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="caption">What are you looking for?</Label>
              <Input
                data-testid="discover-query"
                className="input-warm mt-1.5 h-10"
                placeholder="hardware store, plumber, butcher…"
                value={discoverQuery}
                onChange={(e) => setDiscoverQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runDiscover(); }}
              />
            </div>

            <div>
              <Label className="caption">Where?</Label>
              <div className="mt-1.5 flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="discover-use-location"
                  onClick={requestGeo}
                  className={`rounded-full border h-10 px-3 inline-flex items-center gap-1.5 shrink-0 ${
                    discoverCoords
                      ? "border-hearth-sage bg-hearth-sage/10 text-hearth-sageDark"
                      : "border-hearth-line bg-hearth-paper text-hearth-ink hover:bg-hearth-stone"
                  }`}
                >
                  <MapPin size={14} /> {discoverCoords ? "Using my location" : "Use my location"}
                </Button>
                <Input
                  data-testid="discover-location-text"
                  className="input-warm h-10"
                  placeholder="or type suburb, city or postcode (e.g. 2000 or Sydney NSW)"
                  value={discoverLocText}
                  onChange={(e) => { setDiscoverLocText(e.target.value); if (e.target.value) setDiscoverCoords(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") runDiscover(); }}
                />
              </div>
              <p className="caption mt-1.5 text-hearth-mute">
                Tip: granting location gives you tighter results. Or type a
                postcode / suburb / city — we&apos;ll search &ldquo;{discoverQuery || "your query"} near {discoverLocText || "…"}&rdquo;.
              </p>
            </div>

            <Button
              data-testid="discover-search"
              onClick={runDiscover}
              disabled={discoverLoading}
              className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white px-5 h-10 w-full sm:w-auto"
            >
              {discoverLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Search size={14} className="mr-1.5" />}
              Search Google Places
            </Button>

            {discoverResults.length > 0 && (
              <div className="space-y-2 pt-2" data-testid="discover-results">
                <p className="caption">{discoverResults.length} match{discoverResults.length === 1 ? "" : "es"} — tap to add to your vendors</p>
                {discoverResults.map((r) => (
                  <div key={r.place_id} data-testid={`discover-result-${r.place_id}`}
                       className="border border-hearth-line/70 rounded-xl p-4 bg-hearth-paper">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-display text-xl text-hearth-ink leading-tight">{r.name}</h4>
                          <span className="chip bg-hearth-stone text-hearth-mute">{r.suggested_type}</span>
                          {r.rating && (
                            <span className="inline-flex items-center gap-1 text-xs text-hearth-amber">
                              <Star size={12} fill="currentColor" /> {r.rating} <span className="text-hearth-mute font-mono">({r.rating_count || 0})</span>
                            </span>
                          )}
                        </div>
                        {r.address && (
                          <p className="mt-1 text-sm text-hearth-mute flex items-start gap-1.5">
                            <MapPin size={12} className="mt-0.5 shrink-0" /> {r.address}
                          </p>
                        )}
                        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-hearth-mute">
                          {r.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {r.phone}</span>}
                          {r.website && <a href={ensureUrl(r.website)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-hearth-clayDark"><Globe size={11} /> website</a>}
                          {r.maps_url && <a href={ensureUrl(r.maps_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-hearth-clayDark">Google Maps ↗</a>}
                        </div>
                      </div>
                      <Button
                        data-testid={`discover-import-${r.place_id}`}
                        onClick={() => importOne(r)}
                        disabled={!!importing[r.place_id]}
                        className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white h-9 px-4 shrink-0"
                      >
                        {importing[r.place_id] ? <Loader2 size={13} className="animate-spin" /> : <><Plus size={13} className="mr-1" />Add</>}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDiscoverOpen(false)} className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl border-hearth-line bg-hearth-paper max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">{editId ? "Edit vendor" : "Add a vendor"}</DialogTitle>
            <DialogDescription className="sr-only">Fill or scan the details for a shop, supplier, or service provider your household uses.</DialogDescription>
          </DialogHeader>

          {/* v27.19 — Scan a business card / shop-front photo → prefill fields.
              Hidden when editing so an existing vendor isn't accidentally overwritten. */}
          {!editId && (
            <ScanStrip
              testId="vendor-scan"
              label="Snap a business card — we'll fill the basics."
              buttonLabel="Scan card"
              endpoint="/vendors/scan-card"
              confidence={scanConfidence}
              onConfidence={setScanConfidence}
              onScanned={applyScannedCard}
              successPrefix="Filled from your card"
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="caption">Name</Label>
              <Input data-testid="vendor-form-name" className="input-warm mt-1.5 h-10" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Whole Foods Market" />
            </div>
            <div>
              <Label className="caption">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="input-warm mt-1.5 h-10 rounded-xl" data-testid="vendor-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="caption">Distance (km)</Label>
              <Input type="number" step="0.1" className="input-warm mt-1.5 h-10" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label className="caption">Address</Label>
              <Input className="input-warm mt-1.5 h-10" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div>
              <Label className="caption">Phone</Label>
              <Input className="input-warm mt-1.5 h-10" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+61 4XX XXX XXX" />
            </div>
            <div>
              <Label className="caption">Website</Label>
              <Input className="input-warm mt-1.5 h-10" value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
            </div>
            <div className="md:col-span-2">
              <Label className="caption">Online ordering URL</Label>
              <Input className="input-warm mt-1.5 h-10" value={form.online_order_url || ""} onChange={(e) => setForm({ ...form, online_order_url: e.target.value })} placeholder="https://shop.example.com" />
            </div>

            {form.type === "Online store" && (
              <div className="md:col-span-2" data-testid="vendor-search-url-block">
                <Label className="caption">Product search URL — use {"{q}"} where the item name goes</Label>
                <Input
                  data-testid="vendor-search-url"
                  className="input-warm mt-1.5 h-10 font-mono text-xs"
                  value={form.search_url_template || ""}
                  onChange={(e) => setForm({ ...form, search_url_template: e.target.value })}
                  placeholder="https://www.amazon.com.au/s?k={q}"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className="caption text-hearth-mute self-center mr-1">Quick presets:</span>
                  {ONLINE_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      data-testid={`vendor-search-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setForm({
                        ...form,
                        name: form.name || p.label,
                        search_url_template: p.url,
                      })}
                      className="chip bg-hearth-stone text-hearth-ink hover:bg-hearth-line transition"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <p className="caption text-hearth-mute mt-2 italic">
                  Grocery items will show a small link icon that opens this URL with the item name filled in.
                </p>
              </div>
            )}

            <div className="md:col-span-2">
              <Label className="caption">Categories sold</Label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {CATS.map((c) => {
                  const active = form.categories.includes(c);
                  return (
                    <button key={c} type="button" onClick={() => toggleCat(c)} data-testid={`vendor-cat-${c}`}
                      className={`chip transition ${active ? "bg-hearth-clay text-white" : "bg-hearth-stone text-hearth-mute hover:bg-hearth-line"}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="md:col-span-2">
              <Label className="caption">Opening hours</Label>
              <div className="grid grid-cols-7 gap-1.5 mt-2">
                {DAYS.map(([k, l]) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase tracking-wider text-hearth-mute text-center mb-1">{l}</div>
                    <Input data-testid={`vendor-hours-${k}`} className="input-warm h-9 text-[11px] text-center font-mono"
                      value={form.hours?.[k] || ""}
                      onChange={(e) => setForm({ ...form, hours: { ...form.hours, [k]: e.target.value } })}
                      placeholder="8-21" />
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <Label className="caption">Notes</Label>
              <Textarea className="input-warm mt-1.5" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="vendor-form-save" onClick={save} className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
